import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Plus, Loader2, X } from "lucide-react";
import { formatFullCurrency, STATUS_SELECT_OPTIONS } from "./projectUtils";

export function ProjectCreateDialog({
  open, onOpenChange, form, setForm, balances, yearFilter, onSubmit, loading,
}) {
  const uniqueTypes = [...new Set(balances.map(b => b.project_type).filter(Boolean))].sort();
  const uniqueManagers = [...new Set(balances.map(b => b.project_manager).filter(Boolean))].sort();
  const income = (form.subsidy || 0) + (form.financial_savings || 0) + (form.tax_deduction || 0);
  const balance = income - (form.expenses || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />Nuevo Proyecto I+D
          </DialogTitle>
          <DialogDescription>Rellena los datos del nuevo proyecto. Los campos marcados con * son obligatorios.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label>Nombre del proyecto *</Label>
              <Input
                value={form.project_name}
                onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                placeholder="Ej: Proyecto CDTI 2026 - IA en Sostenibilidad"
                data-testid="create-project-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo de proyecto *</Label>
              <Input
                value={form.project_type}
                onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                placeholder="Ej: CDTI, HORIZON, Propio..."
                list="project-types-list"
                data-testid="create-project-type"
              />
              <datalist id="project-types-list">
                {uniqueTypes.map(t => <option key={t} value={t} />)}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label>Responsable del proyecto</Label>
              <Input
                value={form.project_manager}
                onChange={(e) => setForm({ ...form, project_manager: e.target.value })}
                placeholder="Ej: Ana García"
                list="managers-list"
                data-testid="create-project-manager"
              />
              <datalist id="managers-list">
                {uniqueManagers.map(m => <option key={m} value={m} />)}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="create-status"><SelectValue placeholder="Seleccionar estado" /></SelectTrigger>
                <SelectContent>
                  {STATUS_SELECT_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Año contable</Label>
              <div className="flex items-center h-10 px-3 rounded-md border bg-muted font-semibold text-sm">{yearFilter}</div>
            </div>
            <div className="space-y-2">
              <Label>Año inicio</Label>
              <Input type="number" value={form.year_start}
                onChange={(e) => setForm({ ...form, year_start: e.target.value })}
                placeholder="2024" data-testid="create-year-start" />
            </div>
            <div className="space-y-2">
              <Label>Año fin</Label>
              <Input type="number" value={form.year_end}
                onChange={(e) => setForm({ ...form, year_end: e.target.value })}
                placeholder="2027" data-testid="create-year-end" />
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-muted-foreground mb-3">Datos económicos (€)</p>
            <div className="grid grid-cols-2 gap-4">
              {[
                ["budget", "Presupuesto", "create-budget"],
                ["financing", "Financiación", "create-financing"],
                ["expenses", "Gasto Real", "create-expenses"],
                ["subsidy", "Subvención", "create-subsidy"],
                ["financial_savings", "Ahorro Financiero", "create-financial-savings"],
                ["tax_deduction", "Deducción Fiscal", "create-tax-deduction"],
              ].map(([key, label, testid]) => (
                <div key={key} className="space-y-2">
                  <Label>{label} (€)</Label>
                  <Input
                    type="number" min="0"
                    value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: parseFloat(e.target.value) || 0 })}
                    data-testid={testid}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Ingresos (€) <span className="text-xs text-muted-foreground font-normal">— Subv. + Ahorro + Deducción</span>
              </Label>
              <div className="flex items-center h-10 px-3 rounded-md border bg-blue-50 dark:bg-blue-900/20 font-semibold text-blue-700 dark:text-blue-300">
                {formatFullCurrency(income)}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Balance (€) <span className="text-xs text-muted-foreground font-normal">— Ingresos - Gastos</span>
              </Label>
              <div className={`flex items-center h-10 px-3 rounded-md border font-semibold ${
                balance >= 0
                  ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300"
                  : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300"
              }`}>{formatFullCurrency(balance)}</div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Observaciones, entidad financiadora, estado de la solicitud..."
              data-testid="create-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="w-4 h-4 mr-2" />Cancelar
          </Button>
          <Button onClick={onSubmit} disabled={loading} data-testid="create-project-submit">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Crear Proyecto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
